import { CleanTime, CleanTimePayload } from 'Interfaces/ReferenceInterfaces/CleanTimeInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { cleanTimeScreenServices } from 'services/ReferenceScreenServices/cleanTimeServices';

export class CleanTimeStore {
  cleanTimes: CleanTime[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedCleanTime: CleanTime | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  fetchCleanTimes = async (page = 1, pageSize = 10000) => {
    this.isLoading = true;
    try {
      const response = await cleanTimeScreenServices.getAllCleanTimes(page, pageSize);
      runInAction(() => {
        this.cleanTimes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching clean times');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  pollCleanTimes = async (page = 1, pageSize = 10000) => {
    this.isPolling = true;
    try {
      const response = await cleanTimeScreenServices.getAllCleanTimes(page, pageSize);
      runInAction(() => {
        if (JSON.stringify(this.cleanTimes) !== JSON.stringify(response.data)) {
          this.cleanTimes = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      if (this.environment === 'development') {
        console.error('Error polling clean times:', error);
      }
      toast.error('Error polling clean times!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  createCleanTime = async (cleanTimeData: CleanTimePayload): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await cleanTimeScreenServices.createCleanTime(cleanTimeData);
      if (response.status) {
        toast.success('Clean time created successfully');
        await this.fetchCleanTimes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to create clean time');
        return false;
      }
    } catch (error) {
      toast.error('Error creating clean time');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateCleanTime = async (id: number, cleanTimeData: CleanTimePayload): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await cleanTimeScreenServices.updateCleanTime(id, cleanTimeData);
      if (response.status) {
        toast.success('Clean time updated successfully');
        await this.fetchCleanTimes(this.pagination.currentPage);
        return true;
      } else {
        toast.error(response.message || 'Failed to update clean time');
        return false;
      }
    } catch (error) {
      toast.error('Error updating clean time');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteCleanTime = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await cleanTimeScreenServices.deleteCleanTime(id);
      if (response.status) {
        toast.success('Clean time deleted successfully');

        const isLastItemOnPage = this.cleanTimes.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchCleanTimes(this.pagination.currentPage - 1);
        } else {
          await this.fetchCleanTimes(this.pagination.currentPage);
        }
      } else {
        toast.error(response.message || 'Failed to delete clean time');
      }
    } catch (error) {
      toast.error('Error deleting clean time');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedCleanTime = (cleanTime: CleanTime | null) => {
    this.selectedCleanTime = cleanTime;
  };

  refreshData = async (page = 1, pageSize = 10000) => {
    await this.fetchCleanTimes(page, pageSize);
  };
}

export const cleanTimeStore = new CleanTimeStore();
